import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { attachmentsApi } from '@/lib/api';
import { CATEGORY_LABELS } from '@/constants/categories';
import { Calendar, Building2, Tag, CreditCard, FileText, StickyNote, Clock, Paperclip, ArrowRightLeft } from 'lucide-react';

export function EntryDetailDialog({ entry, siteName, open, onOpenChange }) {
  const [attachments, setAttachments] = useState([]);

  useEffect(() => {
    if (entry?._id && open) {
      attachmentsApi.list(entry._id).then(r => setAttachments(r.data)).catch(() => setAttachments([]));
    } else {
      setAttachments([]);
    }
  }, [entry?._id, open]);

  if (!entry) return null;

  const isIncome = entry.entry_type === 'income';
  const typeColor = isIncome ? 'text-emerald-700 dark:text-emerald-400' : 'text-red-700 dark:text-red-400';
  const typeBg = isIncome ? 'bg-emerald-50 dark:bg-emerald-950/30' : 'bg-red-50 dark:bg-red-950/30';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="entry-detail-dialog">
        <DialogHeader>
          <DialogTitle className="text-lg font-bold tracking-tight">Entry Details</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          {/* Amount highlight */}
          <div className={`flex items-center justify-between p-4 rounded-lg ${typeBg}`}>
            <div>
              <span className={`text-xs font-semibold uppercase tracking-wider ${typeColor}`}>
                {isIncome ? 'Money In' : 'Money Out'}
              </span>
              {entry.transfer_id && (
                <span className="text-xs px-1.5 py-0.5 ml-2 bg-blue-50 dark:bg-blue-950/30 text-blue-700 dark:text-blue-400 rounded inline-flex items-center gap-0.5">
                  <ArrowRightLeft className="w-2.5 h-2.5" />Transfer
                </span>
              )}
            </div>
            <span className={`text-2xl font-bold font-mono ${typeColor}`} data-testid="entry-detail-amount">
              {'\u20B9'}{entry.amount?.toLocaleString('en-IN')}
            </span>
          </div>

          {/* Details grid */}
          <div className="grid grid-cols-2 gap-3">
            <DetailRow icon={Calendar} label="Date" value={entry.date} testId="entry-detail-date" />
            <DetailRow icon={Building2} label="Site" value={siteName || '-'} testId="entry-detail-site" />
            <DetailRow icon={Tag} label="Category" value={CATEGORY_LABELS[entry.category] || entry.category} testId="entry-detail-category" capitalize />
            <DetailRow icon={Tag} label="Sub-Category" value={entry.sub_category || '-'} testId="entry-detail-subcategory" capitalize />
            <DetailRow icon={Building2} label="Entity" value={entry.entity_name || '-'} testId="entry-detail-entity" />
            <DetailRow icon={CreditCard} label="Payment Mode" value={(entry.payment_mode || '').toUpperCase()} testId="entry-detail-mode" />
            {entry.bank_account_name && (
              <DetailRow icon={CreditCard} label="Bank Account" value={entry.bank_account_name} testId="entry-detail-bank" />
            )}
            {entry.created_at && (
              <DetailRow icon={Clock} label="Created" value={new Date(entry.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })} testId="entry-detail-created" />
            )}
          </div>

          {/* Notes section */}
          {entry.notes && (
            <div className="border border-border rounded-lg p-3 bg-muted/30" data-testid="entry-detail-notes">
              <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">
                <StickyNote className="w-3.5 h-3.5" /> Notes
              </div>
              <p className="text-sm leading-relaxed whitespace-pre-wrap">{entry.notes}</p>
            </div>
          )}

          {/* Attachments section */}
          {attachments.length > 0 && (
            <div className="border border-border rounded-lg p-3" data-testid="entry-detail-attachments">
              <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                <Paperclip className="w-3.5 h-3.5" /> Attachments ({attachments.length})
              </div>
              <div className="space-y-1.5">
                {attachments.map(att => (
                  <a
                    key={att.file_id}
                    href={attachmentsApi.getUrl(att.storage_path)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm text-primary hover:underline py-1"
                    data-testid={`entry-detail-att-${att.file_id}`}
                  >
                    <FileText className="w-3.5 h-3.5 shrink-0" />
                    <span className="truncate">{att.original_filename}</span>
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function DetailRow({ icon: Icon, label, value, testId, capitalize = false }) {
  return (
    <div className="flex items-start gap-2" data-testid={testId}>
      <Icon className="w-3.5 h-3.5 mt-0.5 text-muted-foreground shrink-0" />
      <div>
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</div>
        <div className={`text-sm font-medium ${capitalize ? 'capitalize' : ''}`}>{value}</div>
      </div>
    </div>
  );
}
